import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PurchaseOrderStatus } from '../../../types/purchasing';

function formatCurrency(value: number, currency = 'SEK') {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function monthToDate(month: string): Date | null {
  if (!/^\d{4}-\d{2}$/.test(month)) return null;
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1);
}

function formatMonthShort(month: string) {
  const d = monthToDate(month);
  if (!d) return month;
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'short',
  }).format(d);
}

const statusLabel: Record<PurchaseOrderStatus, string> = {
  draft: 'Utkast',
  submitted: 'Skickad',
  transferring: 'Överför',
  transferred: 'Överförd',
  transfer_failed: 'Fel',
};

export default function PurchaseOrderMonthDrilldownDrawer(props: {
  open: boolean;
  month: string | null;
  onClose: () => void;
  currency: string;
  season: string | null;
  bucket: 'draft' | 'done' | 'all';
  loading: boolean;
  error: string | null;
  rows: Array<{
    purchaseOrderId: number;
    orderNumber: string;
    supplierName: string | null;
    status: PurchaseOrderStatus;
    totalUnits: number;
    totalCost: number;
  }>;
}) {
  const {
    open,
    month,
    onClose,
    currency,
    season,
    bucket,
    loading,
    error,
    rows,
  } = props;

  const monthLabel = month ? formatMonthShort(month) : '';
  const totalCost = rows.reduce(
    (sum, r) => sum + (Number(r.totalCost) || 0),
    0,
  );
  const totalUnits = rows.reduce(
    (sum, r) => sum + (Number(r.totalUnits) || 0),
    0,
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 360, sm: 520 }, p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h6">Månad {monthLabel}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {season && <Chip size="small" label={`Säsong ${season}`} />}
            <Chip size="small" label={`Vy: ${bucket}`} />
            {month && <Chip size="small" label={month} variant="outlined" />}
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Ordrar
                </Typography>
                <Typography variant="h6">{rows.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Inköpsvärde
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(totalCost, currency)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Enheter
                </Typography>
                <Typography variant="h6">
                  {totalUnits.toLocaleString('sv-SE')}
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
              >
                Ordrar
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Leverantör</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Enheter</TableCell>
                    <TableCell align="right">Värde</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>Ingen data.</TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.purchaseOrderId} hover>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={`/purchasing/purchase-orders/view/${r.purchaseOrderId}`}
                            underline="hover"
                            sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                          >
                            {r.orderNumber}
                          </Link>
                        </TableCell>
                        <TableCell>{r.supplierName || '—'}</TableCell>
                        <TableCell>
                          {statusLabel[r.status] ?? String(r.status)}
                        </TableCell>
                        <TableCell align="right">
                          {Number(r.totalUnits || 0).toLocaleString('sv-SE')}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(r.totalCost, currency)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
